import { useLogger } from '@opengov/psc-react-logger';
import React from 'react';

import {
  FEDERATED_SERVICE_ID_TYPE,
  IAccessibleEntitiesResponseData,
  IContextSwitcherData,
  IContextSwitcherOptions,
  IEntitledEntitiesGraphQlResponse,
  IEntityDetails,
  IGraphQlResponseEntity
} from '../../types';

import { cacheDataInSessionStorage, hasCacheExpired, retrieveCachedDataFromSessionStorage } from './cacheStorageUtils';

import { authenticatedApiFetch } from './fetchUtils';
import { useNavAuth } from './NavAuthProvider';
import { entityHasEntitledApplication } from './utils';

const getAccessibleEntitiesQuery = `
query AccessibleEntities($userUUID: String!) {
  accessibleEntities(where: { userUUID: $userUUID }) {
      id
      entityId
      name
      entityType
      friendlyId
      federatedServiceIds {
          citCommunityId
          financialsCustomerId
          othPortalId
          ckanCustomerId
          procurementId
          assetManagementId
      }
      entityAppEntitlements {
          entitlements {
              entitlementProductKey
              url
              description
              displayName
              openInNewTab
              defaultApp
          }
          entityUuid
      }
  }
}
`;

const createEntityObject = (entity: IGraphQlResponseEntity): IEntityDetails => {
  return {
    entityType: entity.entityType,
    id: entity.id,
    name: entity.name,
    displayName: entity.displayName,
    friendlyId: entity.friendlyId,
    entitledApplications: entity.entityAppEntitlements?.entitlements ?? [],
    federatedServiceIds: entity.federatedServiceIds
  };
};

export const matchActiveEntityById = (
  activeEntityIdType: FEDERATED_SERVICE_ID_TYPE,
  activeEntityId: string,
  entity: IGraphQlResponseEntity
) => {
  // This is a mapping of the FEDERATED_SERVICE_ID_TYPE to the actual field name in the GraphQL schema
  // This is used to identify the active entity in the returned array of entitled
  // entities by using the entity details provided via props
  switch (activeEntityIdType) {
    case 'BY_CIT_COMMUNITY_ID':
      return entity.federatedServiceIds?.citCommunityId === activeEntityId;
    case 'BY_FINANCIALS_CUSTOMER_ID':
      return entity.federatedServiceIds?.financialsCustomerId === activeEntityId;
    case 'BY_OTH_PORTAL_ID':
      return entity.federatedServiceIds?.othPortalId === activeEntityId;
    case 'BY_CKAN_CUSTOMER_ID':
      return entity.federatedServiceIds?.ckanCustomerId === activeEntityId;
    case 'BY_PROCUREMENT_ID':
      return entity.federatedServiceIds?.procurementId === activeEntityId;
    case 'BY_ASSET_MANAGEMENT_ID':
      return entity.federatedServiceIds?.assetManagementId === activeEntityId;
    case 'BY_FRIENDLY_ID':
      return entity.friendlyId === activeEntityId;
    default:
      return false;
  }
};

const ENTITLED_ENTITIES_DATA_CACHE_EXPIRY_INTERVAL = 60 * 1000; // 1 minute

export const buildUserSessionStorageKey = (userUUID: string) =>
  `CONTEXT_SWITCHER_ENTITLED_ENTITIES_DATA_UUID_${userUUID}`;

export const useContextSwitcherData = ({
  activeEntityId,
  activeEntityIdType,
  activeProductKey,
  baseUrl,
  userUUID,
  forcedDefaultApplication,
  forceContextSwitcherVisibility,
  disabled,
  auth0Config,
  onEntitySelect
}: Pick<
  IContextSwitcherOptions,
  | 'activeEntityId'
  | 'activeEntityIdType'
  | 'userUUID'
  | 'baseUrl'
  | 'activeProductKey'
  | 'forcedDefaultApplication'
  | 'forceContextSwitcherVisibility'
  | 'disabled'
  | 'auth0Config'
  | 'onEntitySelect'
>): Partial<IContextSwitcherData> => {
  const logger = useLogger();

  const { authBearerToken, forceRefresh } = useNavAuth();
  const isMounted = React.useRef(true);
  const isFetchingData = React.useRef(false);
  const previousUserUUID = React.useRef(userUUID);

  // Force refresh token when user changes to ensure we have a fresh token for the new user
  React.useEffect(() => {
    if (userUUID && userUUID !== previousUserUUID.current) {
      forceRefresh();
      previousUserUUID.current = userUUID;
    }
  }, [userUUID, forceRefresh]);

  React.useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const { auth0callbackUrl, auth0clientID, auth0domain } = auth0Config || {};

  const hasAuth0Config = !!(auth0clientID && auth0domain && auth0callbackUrl);

  const [loading, setLoading] = React.useState(false);
  const [loadingError, setLoadingError] = React.useState(false);

  // User-scoped cache management functions
  const userCacheKey = React.useMemo(() => {
    return buildUserSessionStorageKey(userUUID);
  }, [userUUID]);

  const retrieveCachedUserEntityData = React.useCallback(() => {
    const cachedData = retrieveCachedDataFromSessionStorage<IAccessibleEntitiesResponseData>(userCacheKey);
    return cachedData;
  }, [userCacheKey, userUUID]);

  const cacheUserEntityData = React.useCallback(
    (entityData: IAccessibleEntitiesResponseData) => {
      logger.info('Caching entity data');
      return cacheDataInSessionStorage<IAccessibleEntitiesResponseData>(
        userCacheKey,
        entityData,
        ENTITLED_ENTITIES_DATA_CACHE_EXPIRY_INTERVAL
      );
    },
    [userCacheKey, userUUID]
  );

  const hasUserCacheExpired = React.useCallback(() => {
    return hasCacheExpired(userCacheKey);
  }, [userCacheKey]);

  // Private state for deriving active entity and application data once fetched from GraphQL
  const [entitledEntitiesData, setEntitledEntitiesData] = React.useState<IAccessibleEntitiesResponseData>();

  // Derived state for the active entity and application data
  // This is the groomed data that will be used by the ContextSwitcher component
  const [activeEntity, setActiveEntity] = React.useState<IEntityDetails>();
  const [entitledEntities, setEntitledEntities] = React.useState<IEntityDetails[]>();

  const ACCESS_CONTROL_URL = `${baseUrl}/api/access/graphql`;

  // Derived data effect
  React.useEffect(() => {
    if (entitledEntitiesData && entitledEntitiesData.length === 0 && !loadingError) {
      logger.error('Nothing to display: entitled entities data array is empty');
    }

    if (entitledEntitiesData && entitledEntitiesData.length > 0) {
      // Find the active entity data within the entitled entity data.
      // Match against the entity type and ID value provided in the context switcher options
      const activeEntityData = entitledEntitiesData.find((entity) =>
        matchActiveEntityById(activeEntityIdType, activeEntityId, entity)
      );

      if (activeEntityData) {
        const updatedActiveEntity = createEntityObject(activeEntityData);
        setActiveEntity(updatedActiveEntity);

        if (forcedDefaultApplication && !entityHasEntitledApplication(updatedActiveEntity, forcedDefaultApplication)) {
          logger.error(`The active entity does not have the forced default application: ${forcedDefaultApplication}`);
        }
      } else {
        logger.error(`Could not find the active entity "${activeEntityId}" in the entitled entities data`);
      }

      // Extract an array of entitled entities from the data.
      // If a forced default application is provided, filter the entities to only include those with that application
      const updatedEntitledEntities = forcedDefaultApplication
        ? entitledEntitiesData
            .map(createEntityObject)
            .filter((entity) => entityHasEntitledApplication(entity, forcedDefaultApplication))
        : entitledEntitiesData.map(createEntityObject);

      setEntitledEntities(updatedEntitledEntities);
    }
  }, [entitledEntitiesData, loadingError, activeEntityId, activeEntityIdType]);

  const handleFetchResponse = (data: IEntitledEntitiesGraphQlResponse) => {
    if (!isMounted.current || !data) return;

    const fetchedAccessibleEntities = data.data?.accessibleEntities;

    if (fetchedAccessibleEntities) {
      setEntitledEntitiesData(fetchedAccessibleEntities);
      cacheUserEntityData(fetchedAccessibleEntities);
      setLoading(false);
    }

    // GraphQL errors are returned as an array of errors in the response data
    // Since this is not a network error, the promise is still resolved,
    // so we manually throw an error to trigger the catch block and update the UI
    if (data.errors?.length) {
      const graphQlErrors = data.errors;
      throw new Error(JSON.stringify(graphQlErrors));
    }

    // Reset the loading error state if a subsequent fetch return with no errors
    if (!data.errors?.length && loadingError) {
      setLoadingError(false);
    }
  };

  React.useEffect(() => {
    // Skip the fetch entirely when the context switcher is disabled — no need to populate
    // state that will never be displayed.
    if (disabled) return;

    // Since the userUUID is required to fetch the entitled entities data, return early if it's not provided
    if (!userUUID) return;

    // Cached data should also not be used in this case, since if it needs updating, a token is still required
    if (!authBearerToken && hasAuth0Config) return;

    const fetchAccessibleEntitiesData = async () => {
      // Prevent multiple simultaneous fetches
      // This could happen if several prop values change at once
      if (isFetchingData.current) return;

      isFetchingData.current = true;
      logger.info('Fetching accessible entities', {
        hasToken: !!authBearerToken,
        url: ACCESS_CONTROL_URL
      });

      try {
        const data = await authenticatedApiFetch<IEntitledEntitiesGraphQlResponse>(
          ACCESS_CONTROL_URL,
          authBearerToken,
          {
            method: 'POST',
            body: {
              query: getAccessibleEntitiesQuery,
              variables: { userUUID: userUUID }
            },
            includeErrorHandling: false // GraphQL errors are handled in the response
          }
        );

        logger.info('Successfully fetched accessible entities', {
          hasData: !!data?.data?.accessibleEntities,
          entityCount: data?.data?.accessibleEntities?.length ?? 0
        });

        return data;
      } catch (error) {
        logger.error('Failed to fetch accessible entities', {
          error,
          url: ACCESS_CONTROL_URL
        });
        throw error;
      } finally {
        isFetchingData.current = false;
      }
    };

    const cachedEntitledEntitiesData = retrieveCachedUserEntityData();
    const cacheExpired = hasUserCacheExpired();

    if (cachedEntitledEntitiesData) {
      setEntitledEntitiesData(cachedEntitledEntitiesData.data);
    }

    // No background data update needed if the cache is not expired
    if (!cacheExpired) {
      return;
    }

    // Only display the loading spinner if there is no cached data to display
    // Otherwise, display the cached data while quietly fetching new API data
    if (!cachedEntitledEntitiesData) {
      setLoading(true);
    }

    fetchAccessibleEntitiesData()
      .then(handleFetchResponse)
      .catch((error) => {
        if (!isMounted.current) return;
        setLoadingError(true);
        setLoading(false);
        logger.error('Error processing accessible entities response', {
          error
        });
      });
  }, [userUUID, authBearerToken, hasAuth0Config, disabled]);

  return {
    entitledEntities,
    activeEntity,
    activeProductKey,
    loading,
    loadingError,
    forcedDefaultApplication,
    forceContextSwitcherVisibility,
    disabled,
    onEntitySelect
  };
};
